import React, { useState, useEffect, useRef } from "react";

/**
 * DateRangePicker
 * Props:
 *   startDate     {string} – "YYYY-MM-DD"
 *   endDate       {string} – "YYYY-MM-DD"
 *   onChange      {fn}     – called with ({ startDate, endDate })
 *   maxDate       {string} – "YYYY-MM-DD" (optional, defaults to today)
 *   minDate       {string} – "YYYY-MM-DD" (optional)
 *   maxRangeDays  {number} – max days in a range, inclusive (optional, 0 = no limit)
 *   placeholder   {string}
 *   disabled      {bool}
 */
const DateRangePicker = ({
  startDate = "",
  endDate = "",
  onChange,
  maxDate = "",
  minDate = "",
  maxRangeDays = 0,
  placeholder = "Select date range",
  disabled = false,
  height = "",
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const max = maxDate ? parseDate(maxDate) : today;
  const min = minDate ? parseDate(minDate) : null;

  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState((startDate ? parseDate(startDate) : today).getFullYear());
  const [viewMonth, setViewMonth] = useState((startDate ? parseDate(startDate) : today).getMonth());
  const [hoverDate, setHoverDate] = useState(null);
  // internal selection state
  const [selecting, setSelecting] = useState(null); // null | "start"
  const [internalStart, setInternalStart] = useState(startDate ? parseDate(startDate) : null);
  const [internalEnd, setInternalEnd] = useState(endDate ? parseDate(endDate) : null);

  const containerRef = useRef(null);

  // sync props → internal
  useEffect(() => {
    setInternalStart(startDate ? parseDate(startDate) : null);
    setInternalEnd(endDate ? parseDate(endDate) : null);
  }, [startDate, endDate]);

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHoverDate(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function parseDate(str) {
    const [y, m, d] = str.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setHours(0, 0, 0, 0);
    return dt;
  }

  function formatDate(dt) {
    if (!dt) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  function displayDate(dt) {
    if (!dt) return "";
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }

  const displayValue = () => {
    if (!internalStart && !internalEnd) return placeholder;
    if (internalStart && !internalEnd) return `${displayDate(internalStart)} → ...`;
    return `${displayDate(internalStart)} → ${displayDate(internalEnd)}`;
  };

  // days in current view month
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAYS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const isOutOfRange = (dt) => {
    if (max && dt > max) return true;
    if (min && dt < min) return true;

    // after the first click, only allow dates within maxRangeDays of the start date
    if (maxRangeDays && internalStart && !internalEnd) {
      const diff = Math.abs(Math.round((dt - internalStart) / 86400000));
      if (diff > maxRangeDays - 1) return true;
    }
    return false;
  };

  const handleDayClick = (dt) => {
    if (isOutOfRange(dt)) return;

    if (!internalStart || (internalStart && internalEnd)) {
      // fresh selection – set start
      setInternalStart(dt);
      setInternalEnd(null);
      setSelecting("start");
    } else {
      // second click – set end (or swap if before start)
      let s = internalStart;
      let e = dt;
      if (e < s) { [s, e] = [e, s]; }
      setInternalStart(s);
      setInternalEnd(e);
      setSelecting(null);
      setHoverDate(null);
      onChange({ startDate: formatDate(s), endDate: formatDate(e) });
      setIsOpen(false);
    }
  };

  const isStart = (dt) => internalStart && dt.getTime() === internalStart.getTime();
  const isEnd = (dt) => internalEnd && dt.getTime() === internalEnd.getTime();

  const isInRange = (dt) => {
    const effectiveEnd = internalEnd || (selecting === "start" && hoverDate ? hoverDate : null);
    if (!internalStart || !effectiveEnd) return false;
    const lo = internalStart < effectiveEnd ? internalStart : effectiveEnd;
    const hi = internalStart < effectiveEnd ? effectiveEnd : internalStart;
    return dt > lo && dt < hi;
  };

  const clearRange = (e) => {
    e.stopPropagation();
    setInternalStart(null);
    setInternalEnd(null);
    setSelecting(null);
    onChange({ startDate: "", endDate: "" });
  };

  // build calendar grid
  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(viewYear, viewMonth, d));
  }
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Trigger */}
      <div
        onClick={() => !disabled && setIsOpen((p) => !p)}
        className={` border border-gray-300 rounded cursor-pointer flex justify-between items-center select-none px-2
          ${height ? "" : "py-1"}
          ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white hover:border-indigo-400"}`}
        style={height ? { height } : {}}
      >
        <span className={`text-sm truncate ${!internalStart ? "text-md" : "text-md"}`}>
          {displayValue()}
        </span>
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          {(internalStart || internalEnd) && !disabled && (
            <button
              type="button"
              onClick={clearRange}
              className="text-gray-400 hover:text-red-500 transition-colors text-xs leading-none"
              title="Clear"
            >
              ✕
            </button>
          )}
          <svg className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown calendar */}
      {isOpen && !disabled && (
        <div className="absolute z-[99999] mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-72">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={prevMonth}
              className="p-1 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
            >
              ‹
            </button>
            <span className="text-sm font-semibold text-gray-800">
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className="p-1 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
            >
              ›
            </button>
          </div>

          {/* Instruction hint */}
          <p className="text-xs text-gray-400 text-center mb-2">
            {!internalStart || internalEnd ? "Click to set start date" : "Click to set end date"}
          </p>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map((d) => (
              <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {cells.map((dt, i) => {
              if (!dt) return <div key={`empty-${i}`} />;

              const outOfRange = isOutOfRange(dt);
              const start = isStart(dt);
              const end = isEnd(dt);
              const inRange = isInRange(dt);
              const isToday = dt.getTime() === today.getTime();

              let cellClass = "relative flex items-center justify-center h-8 text-sm cursor-pointer select-none transition-colors ";

              if (outOfRange) {
                cellClass += "text-gray-300 cursor-not-allowed ";
              } else if (start || end) {
                cellClass += "text-white font-semibold z-10 ";
              } else if (inRange) {
                cellClass += "bg-indigo-50 text-indigo-700 ";
              } else {
                cellClass += "text-gray-700 hover:bg-indigo-100 hover:text-indigo-700 rounded-full ";
              }

              // Range bar background (left/right half for start/end caps)
              const showRangeBar = inRange;
              const showLeftBar = end && internalStart && dt > internalStart;
              const showRightBar = start && internalEnd && dt < internalEnd;

              return (
                <div
                  key={i}
                  className={cellClass}
                  onClick={() => !outOfRange && handleDayClick(dt)}
                  onMouseEnter={() => {
                    if (!outOfRange && internalStart && !internalEnd) setHoverDate(dt);
                  }}
                  onMouseLeave={() => setHoverDate(null)}
                >
                  {/* range fill behind cell */}
                  {(showRangeBar || showLeftBar || showRightBar) && (
                    <div
                      className="absolute inset-y-1 bg-indigo-50 z-0"
                      style={{
                        left: showLeftBar ? "0" : showRangeBar ? "0" : "50%",
                        right: showRightBar ? "0" : showRangeBar ? "0" : "50%",
                      }}
                    />
                  )}
                  {/* circle for start/end */}
                  {(start || end) && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      {/* half-fill for range continuity */}
                      {(showRightBar || showLeftBar) && (
                        <div
                          className="absolute inset-y-1 bg-indigo-50 z-0"
                          style={{ left: showLeftBar ? "0" : "50%", right: showRightBar ? "0" : "50%", width: "50%" }}
                        />
                      )}
                      <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center z-10 relative">
                        <span className="text-sm font-semibold text-white">{dt.getDate()}</span>
                      </div>
                    </div>
                  )}
                  {/* normal day number */}
                  {!start && !end && (
                    <span className={`relative z-10 w-8 h-8 flex items-center justify-center rounded-full ${isToday && !inRange ? "border border-indigo-400 text-indigo-600 font-semibold" : ""}`}>
                      {dt.getDate()}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Selected range summary */}
          {(internalStart || internalEnd) && (
            <div className="mt-3 pt-2 border-t border-gray-100 flex justify-between text-xs text-gray-500">
              <span>From: <span className="font-medium text-gray-700">{displayDate(internalStart) || "—"}</span></span>
              <span>To: <span className="font-medium text-gray-700">{displayDate(internalEnd) || "—"}</span></span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;